import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { motion } from 'framer-motion';
import { X, Navigation, ZoomIn, ZoomOut } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './VenueMapModal.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const VenueMapModal = ({ isOpen, onClose, venue }) => {
  const mapRef = useRef(null);
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Set focus to modal for accessibility
      if (modalRef.current) {
        modalRef.current.focus();
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleGetDirections = () => {
    if (venue && venue.address) {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(venue.address)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!isOpen || !venue) return null;

  const hasCoordinates = venue.latitude && venue.longitude;

  return createPortal(
    <motion.div
      className="venue-map-modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        ref={modalRef}
        className="venue-map-modal-content"
        initial={{ scale: 0.9, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 50 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Venue location map"
      >
        <div className="venue-map-modal-header">
          <div className="venue-map-modal-title">
            <h2>{venue.name}</h2>
            <p>{venue.address}</p>
          </div>
          <button
            className="venue-map-modal-close"
            onClick={onClose}
            aria-label="Close map"
          >
            <X size={24} />
          </button>
        </div>

        <div className="venue-map-modal-body">
          {hasCoordinates ? (
            <MapContainer
              center={[parseFloat(venue.latitude), parseFloat(venue.longitude)]}
              zoom={17}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
              ref={mapRef}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[parseFloat(venue.latitude), parseFloat(venue.longitude)]}>
                <Popup>
                  <strong>{venue.name}</strong>
                  <br />
                  {venue.address}
                </Popup>
              </Marker>
            </MapContainer>
          ) : (
            <div className="venue-map-no-coordinates">
              <p>Map coordinates not available for this venue.</p>
              <p>Please use the "Get Directions" button below.</p>
            </div>
          )}
        </div>

        <div className="venue-map-modal-footer">
          <button
            className="venue-map-directions-btn"
            onClick={handleGetDirections}
            aria-label="Get directions to venue"
          >
            <Navigation size={18} />
            Get Directions
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
};

export default VenueMapModal;
